import { ReactNode } from "react";
import { Loader2, ShieldAlert } from "lucide-react";

import { useOrganization } from "@/context/OrganizationContext";
import type { FeatureKey } from "@/constants/permissions";
import { FEATURE_LABELS } from "@/constants/permissions";
import { Card, CardContent } from "@/components/ui/card";

interface RequireFeatureProps {
  feature: FeatureKey;
  children: ReactNode;
}

/**
 * Route-level feature gate. Renders a friendly "no access" state when the
 * member's effective permissions lack the feature (the API enforces the same
 * rule server-side — this is purely UX).
 */
export function RequireFeature({ feature, children }: RequireFeatureProps) {
  const { can, isLoading } = useOrganization();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!can(feature)) {
    return (
      <Card className="mx-auto mt-12 max-w-lg">
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <ShieldAlert className="h-10 w-10 text-muted-foreground" />
          <h2 className="text-lg font-semibold">No access to {FEATURE_LABELS[feature].label}</h2>
          <p className="text-sm text-muted-foreground">
            Your organization role does not include access to this feature. Ask an
            organization admin to grant you the "{FEATURE_LABELS[feature].label}" permission.
          </p>
        </CardContent>
      </Card>
    );
  }

  return <>{children}</>;
}
